import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import cytoscape from 'cytoscape';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    NodeType,
    SynopticNodeData,
} from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { EntityProperties } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    DepositWithFolderAndStandard,
    GenericSynopticItem,
    GraceThdSynopticItem,
    Layer,
    SynopticBayPlanItem,
    SynopticBoxPlanItem,
    SynopticService,
    SynopticZone,
    TableOpenableBoxOrBayPlanEnum,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { SynopticBoxPlanData } from '@connect-services/client/services/broadcast-channel.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { DepositWithStandard } from '@connect-services/client/services/synoptic/export-synoptic-cartouche.service';
import { GenericSynopticGraphBuilderService } from '@connect-services/client/services/synoptic/graph-builder/generic-synoptic-graph-builder.service';
import { GraceThdSynopticGraphBuilderService } from '@connect-services/client/services/synoptic/graph-builder/grace-thd-synoptic-graph-builder.service';
import { SelectedSynoptic } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { DepositsService } from '@connect-services/src/sdk/api';
import { SnackBarService, SnackBarTypeEnum } from '@shared/client/services/snack-bar.service';

type PlanItems = SynopticBayPlanItem[] | SynopticBoxPlanItem[];

interface SynopticContext {
    headlessCytoscape: cytoscape.Core;
    selectedSynoptic: SelectedSynoptic;
}

@Injectable({
    providedIn: 'root',
})
export class OpenableBoxOrBayPlanService {
    private readonly broadcastChannelName = BroadcastChannelNameEnum.SYNOPTIC;
    private readonly depositsService = inject(DepositsService);
    private readonly genericSynopticGraphBuilderService = inject(GenericSynopticGraphBuilderService);
    private readonly graceThdSynopticGraphBuilderService = inject(GraceThdSynopticGraphBuilderService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly synopticService = inject(SynopticService);

    public async openInExternalWindow(layer: Layer, dataEntity: EntityProperties, znUuid?: string): Promise<void> {
        const popup = window.open('', 'plan', 'location=yes,scrollbars=yes,status=yes');
        popup?.focus();

        try {
            const payload: BroadcastChannelEvent<SynopticBoxPlanData> = await this.buildPayload(
                layer,
                dataEntity,
                znUuid,
            );
            this.openExternalPlanWindow(payload, popup);
        } catch (error: unknown) {
            popup?.close();

            if (error instanceof HttpErrorResponse) {
                return;
            }

            if (error instanceof Error) {
                this.snackBarService.openPlainTextSnackBar(error.message, SnackBarTypeEnum.ERROR);

                return;
            }

            this.snackBarService.openErrorSnackBar('errorGET');
        }
    }

    private buildHeadlessCytoscape(
        selectedDeposit: DepositWithStandard,
        selectedSynoptic: SelectedSynoptic,
    ): cytoscape.Core {
        if (selectedDeposit.standard.alias.includes('gracethd')) {
            return cytoscape({
                elements: this.graceThdSynopticGraphBuilderService.buildSynoptic(
                    selectedSynoptic.data as GraceThdSynopticItem[],
                ).elements,
                headless: true,
            });
        }

        return cytoscape({
            elements: this.genericSynopticGraphBuilderService.buildSynoptic(
                selectedSynoptic.data as GenericSynopticItem[],
            ).elements,
            headless: true,
        });
    }

    private async buildPayload(
        layer: Layer,
        dataEntity: EntityProperties,
        znUuid?: string,
    ): Promise<BroadcastChannelEvent<SynopticBoxPlanData>> {
        AssertService.isString(layer.deposit);
        AssertService.isString(dataEntity.uuid);

        const selectedDeposit: DepositWithStandard = this.toDepositWithStandard(
            await firstValueFrom(
                this.depositsService.depositControllerGetDepositWithFolderAndStandard({ id: layer.deposit }),
            ),
        );

        const { cyJson, selectedNodeData, selectedSynoptic } = await this.resolveSelectedSynoptic(
            selectedDeposit,
            layer,
            dataEntity,
            znUuid,
        );
        const planItems = await this.getPlanItems(
            layer,
            selectedDeposit.id,
            dataEntity.uuid,
            selectedSynoptic.znUuid || undefined,
        );

        return {
            additionalData: {
                cyJson,
                isLoading: false,
                planItems,
                selectedDeposit,
                selectedNodeData,
                selectedSynoptic,
            },
            isOpening: true,
            message: BroadcastChannelMessageEnum.CHANGE_BOX_PLAN,
            source: BroadcastChannelSourceEnum.GRAPH,
        };
    }

    private getExternalPlanUrl(): string {
        const projectId = this.projectBloc.value()?.id;
        AssertService.isString(projectId);

        const urlTree = this.router.createUrlTree([
            RoutePathEnum.PROJECTS,
            projectId,
            RoutePathEnum.SYNOPTIC,
            RoutePathEnum.EXT,
            RoutePathEnum.SYNOPTIC_PLAN,
        ]);

        return `${window.location.origin}${this.router.serializeUrl(urlTree)}`;
    }

    private getPlanItems(layer: Layer, depositId: string, entityUuid: string, znUuid?: string): Promise<PlanItems> {
        const editSessionId = this.projectEditModeService.isEditModeActive()
            ? this.projectEditModeService.getEditSession()?.id
            : undefined;

        switch (layer.openableBoxOrBayPlan) {
            case TableOpenableBoxOrBayPlanEnum.BOX_SQL:
                return firstValueFrom(
                    this.synopticService.synopticControllerGetBoxPlan({
                        boxId: entityUuid,
                        depositId,
                        editSessionId,
                        znUuid,
                    }),
                );
            case TableOpenableBoxOrBayPlanEnum.BAY_SQL:
                return firstValueFrom(
                    this.synopticService.synopticControllerGetBayPlan({
                        bayId: entityUuid,
                        depositId,
                        editSessionId,
                        znUuid,
                    }),
                );
            default:
                throw new Error('openableBoxOrBayPlan is not supported');
        }
    }

    private getSelectedNodeData(headlessCytoscape: cytoscape.Core, entityUuid: string): SynopticNodeData {
        const selectedNode = headlessCytoscape
            .nodes()
            .filter((node: cytoscape.NodeSingular) => node.data('uuid') === entityUuid)[0];

        if (!selectedNode) {
            throw new Error('Unable to find the selected node in synoptic graph');
        }

        return selectedNode.data() as SynopticNodeData;
    }

    private getVirtualNodeData(layer: Layer, dataEntity: EntityProperties): SynopticNodeData {
        const entityUuid = dataEntity.uuid;
        AssertService.isString(entityUuid);

        const businessIdentifier = dataEntity.businessIdentifier;
        const label =
            typeof businessIdentifier === 'string' && businessIdentifier.length ? businessIdentifier : entityUuid;
        const typeCode =
            layer.openableBoxOrBayPlan === TableOpenableBoxOrBayPlanEnum.BAY_SQL ? NodeType.BAIE : NodeType.PBO;

        return {
            externalCode: label,
            id: `panel-${entityUuid}`,
            label,
            logicalCodeType: typeCode,
            title: label,
            type: typeCode,
            typeCode,
            uuid: entityUuid,
        };
    }

    private openExternalPlanWindow(payload: BroadcastChannelEvent<SynopticBoxPlanData>, popup: Window | null): void {
        const broadcastChannel = new BroadcastChannel(this.broadcastChannelName);
        const timeout = setTimeout(() => {
            broadcastChannel.close();
        }, 10000);

        broadcastChannel.onmessage = (event: MessageEvent<BroadcastChannelEvent<null>>): void => {
            if (!event.data.isOpening || event.data.source !== BroadcastChannelSourceEnum.SYNOPTIC_PLAN) {
                return;
            }

            clearTimeout(timeout);
            broadcastChannel.postMessage(payload);
            broadcastChannel.close();
        };

        if (popup) {
            popup.location.href = this.getExternalPlanUrl();
            popup.focus();

            return;
        }

        window.open(this.getExternalPlanUrl(), 'plan', 'location=yes,scrollbars=yes,status=yes');
    }

    private prioritizeSynopticZones(synopticZones: SynopticZone[], znUuid?: string): SynopticZone[] {
        const requestedZone = synopticZones.find((synopticZone) => synopticZone.zn_uuid === znUuid);

        if (!requestedZone) {
            return synopticZones;
        }

        return [requestedZone, ...synopticZones.filter((synopticZone) => synopticZone.zn_uuid !== znUuid)];
    }

    private async resolveSelectedSynoptic(
        selectedDeposit: DepositWithStandard,
        layer: Layer,
        dataEntity: EntityProperties,
        znUuid?: string,
    ): Promise<{
        cyJson: JSON;
        selectedNodeData: SynopticNodeData;
        selectedSynoptic: SelectedSynoptic;
    }> {
        const entityUuid = dataEntity.uuid;
        AssertService.isString(entityUuid);

        const synopticZones = await firstValueFrom(
            this.synopticService.synopticControllerGetZones({ depositId: selectedDeposit.id }),
        );
        const prioritizedZones = this.prioritizeSynopticZones(synopticZones, znUuid);
        const editSessionId = this.projectEditModeService.isEditModeActive()
            ? this.projectEditModeService.getEditSession()?.id
            : undefined;
        let firstAvailableSynopticContext: SynopticContext | undefined;

        for (const synopticZone of prioritizedZones) {
            const synoptic = await firstValueFrom(
                this.synopticService.synopticControllerGetOne({
                    depositId: selectedDeposit.id,
                    editSessionId,
                    tableName: synopticZone.table_name,
                    znUuid: synopticZone.zn_uuid,
                }),
            );

            const selectedSynoptic: SelectedSynoptic = { ...synoptic, znUuid: synopticZone.zn_uuid };
            const headlessCytoscape = this.buildHeadlessCytoscape(selectedDeposit, selectedSynoptic);
            if (!firstAvailableSynopticContext) {
                firstAvailableSynopticContext = { headlessCytoscape, selectedSynoptic };
            }

            try {
                const selectedNodeData = this.getSelectedNodeData(headlessCytoscape, entityUuid);

                return {
                    cyJson: headlessCytoscape.json() as unknown as JSON,
                    selectedNodeData,
                    selectedSynoptic,
                };
            } catch {
                continue;
            }
        }

        const selectedSynoptic: SelectedSynoptic = firstAvailableSynopticContext?.selectedSynoptic ?? {
            data: [],
            znUuid: znUuid ?? '',
        };
        const headlessCytoscape =
            firstAvailableSynopticContext?.headlessCytoscape ??
            this.buildHeadlessCytoscape(selectedDeposit, selectedSynoptic);
        const selectedNodeData = this.getVirtualNodeData(layer, dataEntity);
        headlessCytoscape.add({ data: selectedNodeData });

        return {
            cyJson: headlessCytoscape.json() as unknown as JSON,
            selectedNodeData,
            selectedSynoptic,
        };
    }

    private toDepositWithStandard(deposit: DepositWithFolderAndStandard): DepositWithStandard {
        if (!deposit.standard) {
            throw new Error('Deposit standard is required');
        }

        return {
            ...deposit,
            depositFolder: deposit.depositFolder.id,
            standard: deposit.standard,
        } as unknown as DepositWithStandard;
    }
}
